"use client";

import * as React from "react";
import { MusicIcon, PlayIcon, UserRoundIcon } from "lucide-react";
import type { WidgetSection, WidgetSectionItem } from "@gerama/types";
import { cn, formatDuration } from "@gerama/utils";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  type CarouselApi,
} from "../carousel";
import { isPlayable, itemHref, moreHref } from "./feed-links";
import { NowPlayingBars } from "../now-playing-bars";
import { categoryColor, categoryGradient, categoryIcon } from "../category-art";

/**
 * Home feed ("Page Builder") renderer shared by the public site and the
 * admin preview — so what an admin previews is literally what users see.
 * Spotify-style: shelves of cards with a hover play button, a compact
 * shortcut grid, hero slider/banners, and a featured item (one hero, or two cards
 * side by side — a carousel once the row is too narrow for both).
 *
 * Framework-agnostic: links go through `renderLink` (the site passes
 * `next/link`), playback through `onPlay`.
 */

/**
 * Card/row sizing per surface — a TV shelf is bigger and sparser than a phone's.
 * `auto` is the public site: phone sizing below `md`, desktop sizing above it,
 * picked by CSS so the server-rendered HTML is already right on a phone (no
 * layout jump after hydration). On a phone the shelves also run edge to edge,
 * like a native app, instead of stopping at the page gutter.
 */
export type FeedDensity = "mobile" | "desktop" | "tv" | "auto";

export interface FeedLinkProps {
  href: string;
  className?: string;
  title?: string;
  children: React.ReactNode;
}

export interface FeedCallbacks {
  renderLink?: (props: FeedLinkProps) => React.ReactNode;
  /** Song/video items call this instead of navigating when their play button is pressed. */
  onPlay?: (item: WidgetSectionItem) => void;
  /** The item currently playing — its play button shows as active. */
  playingId?: string | number | null;
  /** Whether playback is running (the playing item's equalizer freezes while paused). */
  isPlaying?: boolean;
}

interface FeedContext extends FeedCallbacks {
  density: FeedDensity;
}

const Ctx = React.createContext<FeedContext>({ density: "desktop" });

function isActive(item: WidgetSectionItem, playingId: FeedCallbacks["playingId"]) {
  return playingId != null && playingId === (item.target?.id ?? item.id);
}

function DefaultLink({ href, className, title, children }: FeedLinkProps) {
  return (
    <a href={href} className={className} title={title}>
      {children}
    </a>
  );
}

function ItemLink({
  item,
  className,
  children,
}: {
  item: WidgetSectionItem;
  className?: string;
  children: React.ReactNode;
}) {
  const { renderLink } = React.useContext(Ctx);
  const href = itemHref(item);
  if (!href) return <div className={className}>{children}</div>;
  const Link = renderLink ?? DefaultLink;
  return <>{Link({ href, className, title: item.title, children })}</>;
}

function Cover({
  src,
  round,
  className,
  fallback = "music",
  priority,
}: {
  src: string | null | undefined;
  round?: boolean;
  className?: string;
  fallback?: "music" | "artist";
  /** Above-the-fold cover (LCP candidate): load eagerly, high fetch priority. */
  priority?: boolean;
}) {
  const Icon = fallback === "artist" ? UserRoundIcon : MusicIcon;
  return (
    <div
      className={cn(
        "bg-muted relative overflow-hidden shadow-lg shadow-black/30",
        round ? "rounded-full" : "rounded-md",
        className,
      )}
    >
      {src ? (
        // eslint-disable-next-line @next/next/no-img-element -- imgproxy already sized it
        <img
          src={src}
          alt=""
          width={300}
          height={300}
          loading={priority ? "eager" : "lazy"}
          fetchPriority={priority ? "high" : undefined}
          decoding="async"
          className="size-full object-cover"
        />
      ) : (
        <Icon className="text-muted-foreground absolute inset-0 m-auto size-1/3" />
      )}
    </div>
  );
}

function PlayButton({
  item,
  className,
  size = "md",
}: {
  item: WidgetSectionItem;
  className?: string;
  size?: "md" | "lg";
}) {
  const { onPlay, playingId } = React.useContext(Ctx);
  if (!onPlay || !isPlayable(item)) return null;
  const active = isActive(item, playingId);
  return (
    <button
      type="button"
      aria-label={`پخش ${item.title}`}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onPlay(item);
      }}
      className={cn(
        "glass-primary text-primary-foreground flex items-center justify-center rounded-full shadow-xl shadow-black/40 transition-all hover:scale-105",
        size === "lg" ? "size-14" : "size-11",
        active && "ring-primary-foreground/60 ring-2",
        className,
      )}
    >
      <PlayIcon className={cn("fill-current", size === "lg" ? "size-6" : "size-5")} />
    </button>
  );
}

const CARD_WIDTH: Record<FeedDensity, string> = {
  mobile: "w-36",
  desktop: "w-44 xl:w-48",
  tv: "w-60",
  auto: "w-36 md:w-44 xl:w-48",
};

/**
 * Phone shelves on the site bleed to the screen edge: the row cancels the
 * page gutter, the track starts one gutter in, and the last card keeps a
 * gutter after it. Embla reads both edges (container start → first slide,
 * last slide's end margin), so snapping still lines up with the page text.
 */
const BLEED_ROW = "max-md:-mx-4";
const BLEED_TRACK = "max-md:ps-4";
const BLEED_ITEM = "max-md:last:me-4";

/** Touch feedback for anything tappable — a native list cell dims and settles on press. */
const PRESS = "transition-transform active:scale-[0.97] md:active:scale-100";

/**
 * A category as the category page draws it: its colour fading darker, the
 * name in the corner and, tilted at the edge, its picture or a genre icon —
 * so a category without a picture still looks finished.
 */
function CategoryTile({ item, className }: { item: WidgetSectionItem; className?: string }) {
  const Icon = categoryIcon({ id: item.id, name: item.title });
  return (
    <ItemLink
      item={item}
      className={cn(
        "group relative isolate block overflow-hidden rounded-lg p-3 text-sm font-bold text-white shadow-md transition-transform hover:scale-[1.02]",
        className,
      )}
    >
      <span
        aria-hidden
        className="absolute inset-0 -z-10"
        style={{ background: categoryGradient(categoryColor(item)) }}
      />
      <span className="relative line-clamp-2 max-w-[70%]">{item.title}</span>
      <span
        aria-hidden
        className={cn(
          "absolute -end-2 -bottom-2 aspect-square h-[70%] rotate-[25deg] overflow-hidden rounded-md shadow-lg shadow-black/40 transition-transform duration-300 group-hover:rotate-[20deg]",
          !item.image &&
            "flex items-center justify-center bg-white/15 ring-1 ring-white/25 backdrop-blur-sm",
        )}
      >
        {item.image ? (
          // eslint-disable-next-line @next/next/no-img-element -- imgproxy already sized it
          <img
            src={item.image}
            alt=""
            loading="lazy"
            decoding="async"
            className="size-full object-cover"
          />
        ) : (
          <Icon className="size-1/2 -rotate-[25deg] text-white/90" strokeWidth={1.75} />
        )}
      </span>
    </ItemLink>
  );
}

/** Shelf version of `CategoryTile`: same width as the other cards, square. */
function CategoryCard({ item }: { item: WidgetSectionItem }) {
  const { density } = React.useContext(Ctx);
  return (
    <div
      className={cn("shrink-0", density === "auto" ? "md:p-3" : "p-2 sm:p-3", CARD_WIDTH[density])}
    >
      <CategoryTile
        item={item}
        className={cn("aspect-square text-base", density === "auto" && PRESS)}
      />
    </div>
  );
}

/** Shelf card: square cover (round for artists), hover play, title + subtitle. */
function Card({ item, priority }: { item: WidgetSectionItem; priority?: boolean }) {
  const { density } = React.useContext(Ctx);
  const round = item.type === "artist";
  if (item.type === "category") return <CategoryCard item={item} />;
  return (
    <ItemLink
      item={item}
      className={cn(
        "group hover:bg-foreground/5 block shrink-0 rounded-lg transition-colors",
        density === "auto" ? cn("md:p-3", PRESS) : "p-2 sm:p-3",
        CARD_WIDTH[density],
      )}
    >
      <div className="relative">
        <Cover
          src={item.image}
          round={round}
          fallback={round ? "artist" : "music"}
          className="aspect-square w-full"
          priority={priority}
        />
        <PlayButton
          item={item}
          className="absolute end-2 bottom-2 translate-y-2 opacity-0 group-focus-within:translate-y-0 group-focus-within:opacity-100 group-hover:translate-y-0 group-hover:opacity-100"
        />
      </div>
      <p className={cn("mt-2 truncate text-sm font-semibold", round && "text-center")}>
        {item.title}
      </p>
      <p
        className={cn(
          "text-muted-foreground line-clamp-2 min-h-[2lh] text-xs",
          round && "text-center",
        )}
      >
        {item.subtitle || (round ? "هنرمند" : " ")}
      </p>
    </ItemLink>
  );
}

/**
 * How many landscape posters fit in the row, including a peek of the next one.
 * The width is a fraction of the shelf, so a wide desktop and a narrow one
 * both show the same count.
 */
const WIDE_VISIBLE: Record<Exclude<FeedDensity, "auto">, number> = {
  mobile: 1.2,
  desktop: 3.2,
  tv: 4.2,
};

/** `auto`: 1.2 posters on a phone, 2.2 on a tablet, 3.2 on desktop (`gap-3`/`md:gap-4`). */
const WIDE_AUTO_BASIS =
  "basis-[calc((100%_-_0.75rem)/1.2)] md:basis-[calc((100%_-_2rem)/2.2)] lg:basis-[calc((100%_-_3rem)/3.2)]";

/** `gap-4` is 1rem; subtract the gaps that sit between the visible cards. */
function wideBasis(density: Exclude<FeedDensity, "auto">): string {
  const visible = WIDE_VISIBLE[density];
  const gaps = Math.floor(visible);
  return `calc((100% - ${gaps} * 1rem) / ${visible})`;
}

function posterTitle(item: WidgetSectionItem): { text: string; latin: boolean } {
  if (item.titleEn) return { text: item.titleEn, latin: true };
  return { text: item.title, latin: false };
}

/** Landscape poster: picture, then title and artist underneath. */
function WideCard({ item, priority }: { item: WidgetSectionItem; priority?: boolean }) {
  const title = posterTitle(item);
  const subtitle = item.subtitleEn || item.subtitle;
  return (
    <ItemLink item={item} className={cn("group block w-full min-w-0", PRESS)}>
      <div className="relative">
        <Cover
          src={item.image}
          fallback={item.type === "artist" ? "artist" : "music"}
          className={cn(
            "w-full rounded-xl",
            item.type === "slide" ? "aspect-[2/1]" : "aspect-[16/10]",
          )}
          priority={priority}
        />
        <PlayButton
          item={item}
          className="absolute end-3 bottom-3 translate-y-1 opacity-0 group-focus-within:translate-y-0 group-focus-within:opacity-100 group-hover:translate-y-0 group-hover:opacity-100"
        />
      </div>
      <p className="mt-2.5 truncate text-sm font-semibold" dir={title.latin ? "ltr" : undefined}>
        {title.text}
      </p>
      {subtitle ? (
        <p
          className="text-muted-foreground truncate text-xs"
          dir={item.subtitleEn ? "ltr" : undefined}
        >
          {subtitle}
        </p>
      ) : null}
    </ItemLink>
  );
}

/**
 * Two posters share one row once the container can hold both (the site column,
 * not the browser window — a phone-sized admin preview stays a carousel).
 * Narrower than this, they swipe one at a time.
 */
const PAIR_MIN_PX = 448;

function PairLayout({ children }: { children: (wide: boolean) => React.ReactNode }) {
  const ref = React.useRef<HTMLDivElement>(null);
  const [wide, setWide] = React.useState(false);

  React.useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const update = () => setWide(el.clientWidth >= PAIR_MIN_PX);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={ref} className="w-full min-w-0">
      {children(wide)}
    </div>
  );
}

/** One full-width slide at a time, with dots. */
function PagedRow({ children }: { children: React.ReactNode }) {
  const [api, setApi] = React.useState<CarouselApi>();
  const [active, setActive] = React.useState(0);
  const slides = React.Children.toArray(children);

  React.useEffect(() => {
    if (!api) return;
    const onSelect = () => setActive(api.selectedScrollSnap());
    onSelect();
    api.on("select", onSelect);
    return () => {
      api.off("select", onSelect);
    };
  }, [api]);

  return (
    <Carousel opts={{ align: "start", containScroll: "trimSnaps" }} setApi={setApi}>
      <CarouselContent>
        {slides.map((slide, index) => (
          <CarouselItem key={index} className="basis-full">
            {slide}
          </CarouselItem>
        ))}
      </CarouselContent>
      {slides.length > 1 && (
        <div className="mt-2 flex justify-center gap-1.5">
          {slides.map((_, index) => (
            <button
              key={index}
              type="button"
              aria-label={`اسلاید ${(index + 1).toLocaleString("fa")}`}
              onClick={() => api?.scrollTo(index)}
              className={cn(
                "h-1.5 rounded-full transition-all",
                index === active ? "bg-foreground w-6" : "bg-foreground/30 w-1.5",
              )}
            />
          ))}
        </div>
      )}
    </Carousel>
  );
}

/** Exactly two landscape slides: both fill the row, or a carousel when it is narrow. */
function SlidePair({ section, priority }: { section: WidgetSection; priority?: boolean }) {
  return (
    <PairLayout>
      {(wide) =>
        wide ? (
          <div className="grid grid-cols-2 gap-4">
            {section.items.map((item, index) => (
              <WideCard
                key={`${item.type}-${item.id}`}
                item={item}
                priority={priority && index === 0}
              />
            ))}
          </div>
        ) : (
          <PagedRow>
            {section.items.map((item, index) => (
              <WideCard
                key={`${item.type}-${item.id}`}
                item={item}
                priority={priority && index === 0}
              />
            ))}
          </PagedRow>
        )
      }
    </PairLayout>
  );
}

function WideShelf({ section, priority }: { section: WidgetSection; priority?: boolean }) {
  const { density } = React.useContext(Ctx);
  const auto = density === "auto";
  const basis = auto ? undefined : wideBasis(density);
  const visible = WIDE_VISIBLE[auto ? "desktop" : density];
  return (
    <Carousel opts={SHELF_OPTS} className={cn("group/shelf", auto && BLEED_ROW)}>
      <CarouselContent className={cn("pb-2", auto ? cn("gap-3 md:gap-4", BLEED_TRACK) : "gap-4")}>
        {section.items.map((item, index) => (
          <CarouselItem
            key={`${item.type}-${item.id}`}
            className={cn(auto && WIDE_AUTO_BASIS, auto && BLEED_ITEM)}
            style={basis ? { flexBasis: basis } : undefined}
          >
            <WideCard item={item} priority={priority && index < 3} />
          </CarouselItem>
        ))}
      </CarouselContent>
      {section.items.length > Math.floor(visible) && (
        <>
          <CarouselPrevious className="bg-foreground text-background top-[38%] hidden size-10 border-0 shadow-md md:flex" />
          <CarouselNext className="bg-foreground text-background top-[38%] hidden size-10 border-0 shadow-md md:flex" />
        </>
      )}
    </Carousel>
  );
}

function SectionHeader({ section }: { section: WidgetSection }) {
  const { renderLink } = React.useContext(Ctx);
  const href = moreHref(section.more);
  if (!section.showTitle || !section.title) return null;
  const Link = renderLink ?? DefaultLink;
  return (
    <div className="mb-2 flex items-end justify-between gap-2 px-1">
      <h2 className="text-lg font-bold tracking-tight sm:text-2xl">{section.title}</h2>
      {href &&
        Link({
          href,
          className:
            "text-muted-foreground hover:text-foreground shrink-0 text-sm font-semibold hover:underline",
          children: "نمایش همه",
        })}
    </div>
  );
}

const SHELF_OPTS = { align: "start" as const, dragFree: true, containScroll: "trimSnaps" as const };

function Shelf({ section, priority }: { section: WidgetSection; priority?: boolean }) {
  const { density } = React.useContext(Ctx);
  const auto = density === "auto";
  return (
    <Carousel opts={SHELF_OPTS} className={cn("group/shelf", auto && BLEED_ROW)}>
      <CarouselContent className={cn("pb-2", auto ? cn("gap-3 md:gap-1", BLEED_TRACK) : "gap-1")}>
        {section.items.map((item, index) => (
          <CarouselItem
            key={`${item.type}-${item.id}`}
            className={cn("basis-auto", auto && BLEED_ITEM)}
          >
            <Card item={item} priority={priority && index < 3} />
          </CarouselItem>
        ))}
      </CarouselContent>
      {section.items.length > 4 && (
        <>
          <CarouselPrevious className="bg-background/90 top-1/3 hidden size-9 border opacity-0 shadow group-hover/shelf:opacity-100 md:flex" />
          <CarouselNext className="bg-background/90 top-1/3 hidden size-9 border opacity-0 shadow group-hover/shelf:opacity-100 md:flex" />
        </>
      )}
    </Carousel>
  );
}

/** Spotify's top-of-home shortcuts: compact tiles, two per row (four on wide screens). */
function ShortcutGrid({ section }: { section: WidgetSection }) {
  const { density } = React.useContext(Ctx);
  return (
    <div
      className={cn(
        "grid gap-2",
        density === "mobile" || density === "auto" ? "grid-cols-2" : "grid-cols-2 lg:grid-cols-4",
        density === "auto" && "lg:grid-cols-4",
      )}
    >
      {section.items.map((item) =>
        item.type === "category" ? (
          <CategoryTile
            key={`${item.type}-${item.id}`}
            item={item}
            className={cn("h-16", density === "tv" && "h-20", density === "auto" && PRESS)}
          />
        ) : (
          <ItemLink
            key={`${item.type}-${item.id}`}
            item={item}
            className={cn(
              "group bg-foreground/5 hover:bg-foreground/10 active:bg-foreground/15 relative flex items-center gap-3 overflow-hidden rounded-md pe-2 transition-colors",
              density === "auto" && PRESS,
            )}
          >
            <Cover
              src={item.image}
              round={item.type === "artist"}
              className={cn(
                "shrink-0 rounded-none shadow-none",
                density === "tv" ? "size-20" : "size-14",
              )}
            />
            <span className="line-clamp-2 min-w-0 flex-1 text-sm font-semibold">{item.title}</span>
            <PlayButton
              item={item}
              className="size-9 opacity-0 transition-opacity group-hover:opacity-100 [&_svg]:size-4"
            />
          </ItemLink>
        ),
      )}
    </div>
  );
}

/** Numbered track rows. */
function TrackList({ section }: { section: WidgetSection }) {
  const { playingId, isPlaying } = React.useContext(Ctx);
  return (
    <ol className="space-y-0.5">
      {section.items.map((item, index) => (
        <li key={`${item.type}-${item.id}`}>
          <ItemLink
            item={item}
            className="group hover:bg-foreground/5 active:bg-foreground/10 grid grid-cols-[1.5rem_auto_1fr_auto] items-center gap-3 rounded-md px-2 py-1.5"
          >
            <span className="text-muted-foreground text-center text-sm tabular-nums">
              {(index + 1).toLocaleString("fa")}
            </span>
            <span
              className={cn(
                "relative size-11 overflow-hidden",
                item.type === "artist" ? "rounded-full" : "rounded-md",
              )}
            >
              <Cover
                src={item.image}
                round={item.type === "artist"}
                className="size-11 shadow-none"
              />
              {isActive(item, playingId) && <NowPlayingBars playing={isPlaying ?? true} />}
            </span>
            <span className="min-w-0">
              <span
                className={cn(
                  "block truncate text-sm font-medium",
                  isActive(item, playingId) && "text-primary",
                )}
              >
                {item.title}
              </span>
              {item.subtitle && (
                <span className="text-muted-foreground block truncate text-xs">
                  {item.subtitle}
                </span>
              )}
            </span>
            <span
              className="text-muted-foreground flex items-center gap-2 text-xs tabular-nums"
              dir="ltr"
            >
              {item.duration ? formatDuration(item.duration) : null}
            </span>
          </ItemLink>
        </li>
      ))}
    </ol>
  );
}

function HeroSlide({ item, priority }: { item: WidgetSectionItem; priority?: boolean }) {
  const { density } = React.useContext(Ctx);
  return (
    <ItemLink
      item={item}
      className={cn(
        "group relative block w-full shrink-0 snap-center overflow-hidden rounded-xl",
        density === "mobile"
          ? "aspect-[16/9]"
          : density === "auto"
            ? "aspect-[16/9] md:aspect-[21/8]"
            : "aspect-[21/8]",
      )}
    >
      {item.image ? (
        // eslint-disable-next-line @next/next/no-img-element -- imgproxy hero preset
        <img
          src={item.image}
          alt=""
          loading={priority ? "eager" : "lazy"}
          fetchPriority={priority ? "high" : undefined}
          decoding="async"
          className="absolute inset-0 size-full object-cover transition-transform duration-700 group-hover:scale-[1.02]"
        />
      ) : (
        <div className="from-primary/40 to-muted absolute inset-0 bg-gradient-to-bl" />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
      <div className="absolute inset-x-0 bottom-0 flex items-end justify-between gap-4 p-4 sm:p-6">
        <div className="min-w-0 text-white">
          {item.title && (
            <p
              className={cn(
                "font-extrabold drop-shadow",
                density === "mobile"
                  ? "text-xl"
                  : density === "auto"
                    ? "text-xl md:text-3xl"
                    : "text-3xl",
              )}
            >
              {item.title}
            </p>
          )}
          {item.subtitle && <p className="mt-1 truncate text-sm text-white/80">{item.subtitle}</p>}
        </div>
        <PlayButton item={item} size="lg" />
      </div>
    </ItemLink>
  );
}

const HERO_OPTS = { loop: true, align: "start" as const };

function Slider({ section, priority }: { section: WidgetSection; priority?: boolean }) {
  const [api, setApi] = React.useState<CarouselApi>();
  const [active, setActive] = React.useState(0);
  const count = section.items.length;

  React.useEffect(() => {
    if (!api) return;
    const onSelect = () => setActive(api.selectedScrollSnap());
    onSelect();
    api.on("select", onSelect);
    return () => {
      api.off("select", onSelect);
    };
  }, [api]);

  React.useEffect(() => {
    if (!api || count < 2) return;
    const timer = window.setInterval(() => api.scrollNext(), 6000);
    return () => window.clearInterval(timer);
  }, [api, count]);

  return (
    <Carousel opts={HERO_OPTS} setApi={setApi}>
      <CarouselContent>
        {section.items.map((item, index) => (
          <CarouselItem key={String(item.id)}>
            <HeroSlide item={item} priority={priority && index === 0} />
          </CarouselItem>
        ))}
      </CarouselContent>
      {count > 1 && (
        <div className="mt-2 flex justify-center gap-1.5">
          {section.items.map((item, index) => (
            <button
              key={String(item.id)}
              type="button"
              aria-label={`اسلاید ${(index + 1).toLocaleString("fa")}`}
              onClick={() => api?.scrollTo(index)}
              className={cn(
                "h-1.5 rounded-full transition-all",
                index === active ? "bg-foreground w-6" : "bg-foreground/30 w-1.5",
              )}
            />
          ))}
        </div>
      )}
    </Carousel>
  );
}

/** One featured card. A pair is a compact row so both stay on screen; a single item stays the full-width hero. */
function FeaturedCard({
  item,
  section,
  priority,
  paired,
}: {
  item: WidgetSectionItem;
  section: WidgetSection;
  priority?: boolean;
  paired: boolean;
}) {
  const { density } = React.useContext(Ctx);
  const phone = density === "mobile";
  // The site on a phone: the cover with the title and artist under it — a
  // full-width square filled the screen, so the cover stays mid-sized.
  const auto = density === "auto" && !paired;
  return (
    <ItemLink
      item={item}
      className={cn(
        "group relative flex w-full min-w-0 overflow-hidden rounded-xl",
        paired
          ? "items-center gap-4 p-4"
          : auto
            ? cn("flex-col gap-3 p-4 md:flex-row md:items-end md:gap-6 md:p-6", PRESS)
            : phone
              ? "flex-col gap-4 p-4"
              : "items-end gap-6 p-4 sm:p-6",
      )}
    >
      <div
        className="absolute inset-0 -z-10"
        style={{
          background: `linear-gradient(135deg, ${section.bgColor ?? "var(--primary)"} 0%, color-mix(in oklab, ${
            section.bgColor ?? "var(--primary)"
          } 25%, var(--background)) 100%)`,
        }}
      />
      <Cover
        src={item.image}
        priority={priority}
        className={cn(
          "shrink-0",
          paired
            ? "size-24 sm:size-28"
            : auto
              ? "size-44 md:size-48"
              : phone
                ? "aspect-square w-full"
                : density === "tv"
                  ? "size-64"
                  : "size-48",
        )}
      />
      <div className="min-w-0 flex-1 space-y-2">
        <p className="text-xs font-bold tracking-wide text-white/80 uppercase">
          {section.title || (item.type === "video" ? "نماهنگ ویژه" : "پیشنهاد ویژه")}
        </p>
        <p
          className={cn(
            "font-black text-white",
            paired || phone
              ? "text-xl sm:text-2xl"
              : auto
                ? "line-clamp-2 text-xl md:text-4xl lg:text-5xl"
                : "text-4xl lg:text-5xl",
          )}
        >
          {item.title}
        </p>
        {item.subtitle && (
          <p className="truncate text-sm font-semibold text-white/90">{item.subtitle}</p>
        )}
        {item.description && !paired && (
          <p className={cn("max-w-xl text-sm text-white/75", auto && "max-md:hidden")}>
            {item.description}
          </p>
        )}
        <div className="pt-1">
          <PlayButton item={item} size={paired ? "md" : "lg"} />
        </div>
      </div>
    </ItemLink>
  );
}

/**
 * One item is the full-width hero. Two items sit side by side when the row
 * can hold both, and become a swipe carousel when it cannot. Width follows
 * the container (the site column, the phone frame, the admin preview), not
 * the browser viewport — a viewport `md:` grid inside a narrow preview was
 * pushing the second card outside the frame.
 */
function Featured({ section, priority }: { section: WidgetSection; priority?: boolean }) {
  const items = (Array.isArray(section.items) ? section.items : []).slice(0, 2);
  const first = items[0];
  if (!first) return null;
  if (items.length === 1) {
    return <FeaturedCard item={first} section={section} priority={priority} paired={false} />;
  }
  return (
    <PairLayout>
      {(wide) =>
        wide ? (
          <div className="grid grid-cols-2 gap-3">
            {items.map((item, index) => (
              <FeaturedCard
                key={`${item.type}-${item.id}-${index}`}
                item={item}
                section={section}
                priority={priority && index === 0}
                paired
              />
            ))}
          </div>
        ) : (
          <PagedRow>
            {items.map((item, index) => (
              <FeaturedCard
                key={`${item.type}-${item.id}-${index}`}
                item={item}
                section={section}
                priority={priority && index === 0}
                paired={false}
              />
            ))}
          </PagedRow>
        )
      }
    </PairLayout>
  );
}

/** One resolved section, drawn by its `display`. */
export function FeedSection({
  section,
  priority,
}: {
  section: WidgetSection;
  /** First section on the page — its leading images are the LCP candidates. */
  priority?: boolean;
}) {
  const { density } = React.useContext(Ctx);
  const slideRow = section.items.some((item) => item.type === "slide");
  const twoSlides = slideRow && section.items.length === 2;
  const body = (() => {
    switch (section.display) {
      case "banner":
        // A slider is several posters in one row. Two slides fill that row
        // (or swipe, when it is narrow). A banner stays one full-bleed slide.
        return slideRow ? (
          twoSlides ? (
            <SlidePair section={section} priority={priority} />
          ) : (
            <WideShelf section={section} priority={priority} />
          )
        ) : (
          <Slider section={section} priority={priority} />
        );
      case "featured":
        return <Featured section={section} priority={priority} />;
      case "grid":
        return <ShortcutGrid section={section} />;
      case "list":
        return <TrackList section={section} />;
      case "wide":
        return twoSlides ? (
          <SlidePair section={section} priority={priority} />
        ) : (
          <WideShelf section={section} priority={priority} />
        );
      default:
        return <Shelf section={section} priority={priority} />;
    }
  })();
  const hero = section.display === "banner" && !slideRow;
  const tinted = section.bgColor && section.display !== "featured";
  return (
    <section
      className={cn(
        tinted && "rounded-xl p-3 sm:p-4",
        // A tinted band on a phone spans the screen, so its shelves can still reach the edge.
        tinted && density === "auto" && "max-md:-mx-4 max-md:rounded-none max-md:px-4",
      )}
      style={
        tinted
          ? { background: `linear-gradient(180deg, ${section.bgColor}55 0%, transparent 100%)` }
          : undefined
      }
    >
      {section.display !== "featured" && !hero && <SectionHeader section={section} />}
      {body}
    </section>
  );
}

/** The whole feed — the site's home page and the admin's full-page preview. */
export function HomeFeed({
  sections,
  density = "desktop",
  highlightId,
  className,
  ...callbacks
}: FeedCallbacks & {
  sections: WidgetSection[];
  density?: FeedDensity;
  /** Admin preview: outline the widget being hovered in the list. */
  highlightId?: number | null;
  className?: string;
}) {
  const value = React.useMemo(
    () => ({ density, ...callbacks }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- callbacks are stable per render site
    [density, callbacks.renderLink, callbacks.onPlay, callbacks.playingId, callbacks.isPlaying],
  );
  return (
    <Ctx.Provider value={value}>
      <div
        className={cn(
          density === "tv"
            ? "space-y-10"
            : density === "auto"
              ? "space-y-7 md:space-y-8"
              : "space-y-8",
          className,
        )}
      >
        {sections.map((section, index) => (
          <div
            key={section.widgetId}
            className={cn(
              "rounded-xl transition-shadow",
              highlightId === section.widgetId &&
                "ring-primary ring-2 ring-offset-4 ring-offset-transparent",
            )}
          >
            <FeedSection section={section} priority={index < 3} />
          </div>
        ))}
      </div>
    </Ctx.Provider>
  );
}

/** Single-section preview (admin widget editor) with the same context wiring. */
export function FeedSectionPreview({
  section,
  density = "desktop",
  ...callbacks
}: FeedCallbacks & { section: WidgetSection; density?: FeedDensity }) {
  return (
    <Ctx.Provider value={{ density, ...callbacks }}>
      <FeedSection section={section} />
    </Ctx.Provider>
  );
}
